import React from 'react';
import { Modal, message } from 'antd';
import ResumeSection from '../components/ResumeSection';
import ListSectionView from '../com/ListSectionView';
import {
  educationSection,
  skillSection,
  certificateSection,
  languageSection,
  worksSection,
} from '../configs/sectionConfig';
import { buildMonthRangeParams, normalizeAttachmentIds } from '../utils/resumeTransforms';
import Config from '../../../config/Config';
import WidgetAjax from '../../../widget/WidgetAjax';

/**
 * 判断单个表单字段值是否"为空" — 拷贝自 hooks/useResumeSubmit.js 的 isEmptyValue,
 * 在本模块本地复刻避免引入 hooks 文件副作用。行为完全一致:
 *   - undefined / null / '' → true
 *   - [] → true
 *   - dayjs 但 invalid() → true
 *   - 空对象 {} → true
 *   - 其它 → false
 */
const isEmptyValue = (value) => {
  if (value === undefined || value === null || value === '') return true;
  if (Array.isArray(value)) return value.length === 0;
  if (value && typeof value === 'object') {
    if (value.format && value.isValid && !value.isValid()) return true;
    return Object.keys(value).length === 0;
  }
  return false;
};

/**
 * 判断整条记录是否全部字段都为空 — 拷贝自 hooks/useResumeSubmit.js 的 isEmptyRecord。
 * 命中时短路: 关闭编辑态, 不发请求 (与原 saveListSection 各分支等价 + 不发空表单)。
 */
const isEmptyRecord = (record, fields = []) => {
  if (!record || typeof record !== 'object') return true;
  return fields.every((field) => isEmptyValue(record[field.props]));
};

/**
 * 教育 & 能力 模块 (class 组件版)
 *
 * 一次性容纳 5 个子区块,共享同一份 formRef 但各自维护独立的 editingIndex/adding/附件草稿:
 *   1) 教育经历   (EdcHistory / listKey = education)
 *   2) 专业技能   (jobSkill  / listKey = skill)
 *   3) 资格证书   (certificate / listKey = certificate) — 走 ImageUploader 单图附件
 *   4) 语言能力   (language / listKey = language)
 *   5) 个人作品   (works     / listKey = works)       — 走 ImageUploader 多图附件
 *
 * - props:
 *   - resumeState:  由 useResumeData 注入,含 education / skill / certificate / language / works
 *   - options:      由 useResumeOptions 注入,字典 + jobTypeList / areaList (本模块不需要)
 *   - applicantId:  当前用户 id
 *   - onChanged:    任意持久化操作 (add/update/remove) 成功后回调,父页面触发 reloadResume
 *
 * - 不接收任何外部 setter,模块内自管附件草稿 (setCertificateFile / setWorksFile)
 * - 不调用任何 hooks;不引用 useResumeEditor / useResumeSubmit
 *
 * - 提交层 (直接调 WidgetAjax.ajax, 与 useResumeSubmit.saveListSection/deleteListRecord 对齐):
 *   - 教育 (EdcHistory):   add/update/remove_education;ENRDATE → ENRFROM + ENRTO
 *   - 技能 (jobSkill):    add/update/remove_skill
 *   - 证书 (certificate): add/update/remove_certificate;attachments=[objId||attachments]
 *   - 语言 (language):    add/update/remove_language
 *   - 作品 (works):       add/update/remove_works;attachments 经 normalizeAttachmentIds
 *
 * - 删除走 Modal.confirm;status===0 → message.success → 关闭编辑/选择器 → onChanged
 *   失败只 toast,保留编辑态。
 */
class EducationAbilityModule extends React.Component {
  formRef = React.createRef();

  constructor(props) {
    super(props);
    this.state = {
      activeSectionId: undefined, // 当前正在编辑/添加的子区,给上层用于定位
      educationEditingIndex: undefined,
      educationAdding: false,
      skillEditingIndex: undefined,
      skillAdding: false,
      certificateEditingIndex: undefined,
      certificateAdding: false,
      certificateFile: undefined,
      languageEditingIndex: undefined,
      languageAdding: false,
      worksEditingIndex: undefined,
      worksAdding: false,
      worksFile: undefined,
    };
  }

  // ============ 编辑态切换 ============

  setActiveSection = (sectionId) => {
    this.setState({ activeSectionId: sectionId });
  };

  // ============ 删除 ============

  /**
   * 通用删除:弹 Modal.confirm,确认后调 WidgetAjax 走对应 remove_* 接口
   * - section: sectionConfig 中对应的子区
   * - record:  必须有 id (server 已落库条目)
   */
  handleDelete = (section, record) => {
    if (!record || !record.id) {
      message.error('记录缺少 id, 无法删除');
      return;
    }

    Modal.confirm({
      title: '确认删除',
      content: `确定删除这条${section.title}吗？`,
      okText: '确认',
      cancelText: '取消',
      onOk: () => this.handleRemoveRemote(section, record),
    });
  };

  /**
   * 删除走原 parent useResumeSubmit.deleteListRecord 风格:
   *   - 入参只传 record.id, 不传 applicant_id (与 Task 5 对齐 — 删除接口统一由后端
   *     从 access token 解析申请人, 无须冗余 applicant_id)
   *   - 成功关掉该子区编辑态 + 触发 onChanged, 失败只 toast
   */
  handleRemoveRemote = (section, record) => {
    const { onChanged } = this.props;
    const urlMap = {
      [educationSection.id]: `${Config.getBasePath()}/applicant/remove_education`,
      [skillSection.id]: `${Config.getBasePath()}/applicant/remove_skill`,
      [certificateSection.id]: `${Config.getBasePath()}/applicant/remove_certificate`,
      [languageSection.id]: `${Config.getBasePath()}/applicant/remove_language`,
      [worksSection.id]: `${Config.getBasePath()}/applicant/remove_works`,
    };
    const url = urlMap[section.id];
    if (!url) {
      message.error('未知的子区, 无法删除');
      return;
    }

    WidgetAjax.ajax({
      url,
      params: { id: record.id },
      callback: (res) => {
        if (res && res.status === 0) {
          message.success('删除成功');
          // 关掉对应子区的编辑态(以及它自己的附件草稿)
          this.closeEditor(section.id);
          if (typeof onChanged === 'function') onChanged();
          return;
        }
        message.error((res && res.message) || '删除失败');
      },
    });
  };

  // ============ 表单提交 ============

  /**
   * 通用 add/update 入口:对齐 useResumeSubmit.saveListSection 各分支
   * - section:   sectionConfig 子区
   * - values:    ResumeEditorForm.onFinish 的入参
   * - record:    当前正在编辑的记录 (undefined = 新增)
   *
   * 入参构造:
   *   - education: buildMonthRangeParams 把 ENRDATE → ENRFROM + ENRTO
   *   - skill:     原样 {...values}
   *   - certificate: attachments=[objId || attachments]
   *   - language:  原样 {...values}
   *   - works:     attachments 经 normalizeAttachmentIds
   *
   * 顶部 isEmptyRecord(values, editableFields) 短路:
   *   全部可编辑字段都为空 → closeEditor 不发请求
   */
  handleSubmit = (section, values, record) => {
    const { onChanged } = this.props;

    // 1) 空表单短路
    const editableFields = (section.formList || [])
      .filter((field) => field.type !== 'tips' && field.props);
    if (isEmptyRecord(values, editableFields)) {
      this.closeEditor(section.id);
      return;
    }

    // 2) 入参构造 (对齐 useResumeSubmit.saveListSection 各分支)
    const isUpdate = record && record.id;
    let url;
    let finalParams;

    if (section.id === educationSection.id) {
      url = isUpdate
        ? `${Config.getBasePath()}/applicant/update_education`
        : `${Config.getBasePath()}/applicant/add_education`;
      const base = {
        ...values,
        ...buildMonthRangeParams(values, 'ENRDATE', 'ENRFROM', 'ENRTO'),
      };
      finalParams = isUpdate ? { id: record.id, ...base } : { ...base };
    } else if (section.id === skillSection.id) {
      url = isUpdate
        ? `${Config.getBasePath()}/applicant/update_skill`
        : `${Config.getBasePath()}/applicant/add_skill`;
      const base = { ...values };
      finalParams = isUpdate ? { id: record.id, ...base } : { ...base };
    } else if (section.id === certificateSection.id) {
      url = isUpdate
        ? `${Config.getBasePath()}/applicant/update_certificate`
        : `${Config.getBasePath()}/applicant/add_certificate`;
      const certificateFile = this.state.certificateFile;
      const attachments = certificateFile && certificateFile.objId
        ? [certificateFile.objId]
        : certificateFile && certificateFile.attachments
          ? certificateFile.attachments
          : undefined;
      finalParams = isUpdate
        ? { id: record.id, ...values, attachments }
        : { ...values, attachments };
    } else if (section.id === languageSection.id) {
      url = isUpdate
        ? `${Config.getBasePath()}/applicant/update_language`
        : `${Config.getBasePath()}/applicant/add_language`;
      const base = { ...values };
      finalParams = isUpdate ? { id: record.id, ...base } : { ...base };
    } else if (section.id === worksSection.id) {
      url = isUpdate
        ? `${Config.getBasePath()}/applicant/update_works`
        : `${Config.getBasePath()}/applicant/add_works`;
      const worksFile = this.state.worksFile;
      const rawAttachments = Array.isArray(worksFile)
        ? worksFile
        : (worksFile && worksFile.attachments) || [];
      const attachments = normalizeAttachmentIds(rawAttachments);
      finalParams = isUpdate
        ? { id: record.id, ...values, attachments }
        : { ...values, attachments };
    } else {
      message.error('未知的子区, 无法保存');
      return;
    }

    const successText = isUpdate ? '保存成功' : '添加成功';
    const errorText = isUpdate ? '保存失败' : '添加失败';

    WidgetAjax.ajax({
      url,
      params: finalParams,
      callback: (res) => {
        if (res && res.status === 0) {
          message.success(successText);
          this.closeEditor(section.id);
          if (typeof onChanged === 'function') onChanged();
          return;
        }
        // 失败提示 + 保留编辑态 (不调 closeEditor)
        message.error((res && res.message) || errorText);
      },
    });
  };

  // ============ ListSectionView 所需的 callback 包装器 ============

  /**
   * 互斥重置:除 keepSectionId 之外,清掉其余 4 个子区的 editingIndex / adding,
   * 同时清掉 certificateFile / worksFile, 避免上一个子区的附件草稿串到当前表单。
   * 供每个 startXxxEdit / startXxxAdd 在 setState 时合并调用。
   */
  resetOthersExcept = (keepSectionId) => {
    const allSections = [
      educationSection.id,
      skillSection.id,
      certificateSection.id,
      languageSection.id,
      worksSection.id,
    ];
    const flagMap = {
      [educationSection.id]: ['educationEditingIndex', 'educationAdding'],
      [skillSection.id]: ['skillEditingIndex', 'skillAdding'],
      [certificateSection.id]: ['certificateEditingIndex', 'certificateAdding'],
      [languageSection.id]: ['languageEditingIndex', 'languageAdding'],
      [worksSection.id]: ['worksEditingIndex', 'worksAdding'],
    };
    const reset = {
      activeSectionId: undefined,
      certificateFile: undefined,
      worksFile: undefined,
    };
    allSections.forEach((sid) => {
      if (sid === keepSectionId) return;
      const [editingKey, addingKey] = flagMap[sid];
      reset[editingKey] = undefined;
      reset[addingKey] = false;
    });
    return reset;
  };

  // 每个子区各自的 startEdit / startAdd / closeEditor 包装,落到 setActiveSection
  startEducationEdit = (idx) => {
    this.setState({
      ...this.resetOthersExcept(educationSection.id),
      activeSectionId: educationSection.id,
      educationEditingIndex: idx,
      educationAdding: false,
    });
  };

  startEducationAdd = () => {
    this.setState({
      ...this.resetOthersExcept(educationSection.id),
      activeSectionId: educationSection.id,
      educationEditingIndex: undefined,
      educationAdding: true,
    });
  };

  startSkillEdit = (idx) => {
    this.setState({
      ...this.resetOthersExcept(skillSection.id),
      activeSectionId: skillSection.id,
      skillEditingIndex: idx,
      skillAdding: false,
    });
  };

  startSkillAdd = () => {
    this.setState({
      ...this.resetOthersExcept(skillSection.id),
      activeSectionId: skillSection.id,
      skillEditingIndex: undefined,
      skillAdding: true,
    });
  };

  startCertificateEdit = (idx) => {
    const { resumeState = {} } = this.props;
    const list = (resumeState.raw && resumeState.raw.certificates) || [];
    this.setState({
      ...this.resetOthersExcept(certificateSection.id),
      activeSectionId: certificateSection.id,
      certificateEditingIndex: idx,
      certificateAdding: false,
      // 编辑已有证书时, 把这条记录的 attachments 回填到草稿, 这样 ImageUploader
      // 还能看到原图附件; 此时上一个子区的 worksFile 已在上方 reset 中清掉.
      certificateFile: list[idx],
    });
  };

  startCertificateAdd = () => {
    this.setState({
      ...this.resetOthersExcept(certificateSection.id),
      activeSectionId: certificateSection.id,
      certificateEditingIndex: undefined,
      certificateAdding: true,
    });
  };

  startLanguageEdit = (idx) => {
    this.setState({
      ...this.resetOthersExcept(languageSection.id),
      activeSectionId: languageSection.id,
      languageEditingIndex: idx,
      languageAdding: false,
    });
  };

  startLanguageAdd = () => {
    this.setState({
      ...this.resetOthersExcept(languageSection.id),
      activeSectionId: languageSection.id,
      languageEditingIndex: undefined,
      languageAdding: true,
    });
  };

  startWorksEdit = (idx) => {
    const { resumeState = {} } = this.props;
    const list = (resumeState.raw && resumeState.raw.works) || [];
    this.setState({
      ...this.resetOthersExcept(worksSection.id),
      activeSectionId: worksSection.id,
      worksEditingIndex: idx,
      worksAdding: false,
      // 编辑已有作品时, 把这条记录的 attachments 回填到草稿, 让 ImageUploader
      // 看到原图附件; 此时上一个子区的 certificateFile 已在上方 reset 中清掉.
      worksFile: list[idx],
    });
  };

  startWorksAdd = () => {
    this.setState({
      ...this.resetOthersExcept(worksSection.id),
      activeSectionId: worksSection.id,
      worksEditingIndex: undefined,
      worksAdding: true,
    });
  };

  /**
   * 关闭指定子区的编辑态 (不接收外部 setter,模块内自管)
   * - 同时清掉对应附件草稿,避免下一次进入编辑器时残留旧 objId/attachments
   */
  closeEditor = (sectionId) => {
    if (sectionId === educationSection.id) {
      this.setState({
        activeSectionId: undefined,
        educationEditingIndex: undefined,
        educationAdding: false,
      });
      return;
    }
    if (sectionId === skillSection.id) {
      this.setState({
        activeSectionId: undefined,
        skillEditingIndex: undefined,
        skillAdding: false,
      });
      return;
    }
    if (sectionId === certificateSection.id) {
      this.setState({
        activeSectionId: undefined,
        certificateEditingIndex: undefined,
        certificateAdding: false,
        certificateFile: undefined,
      });
      return;
    }
    if (sectionId === languageSection.id) {
      this.setState({
        activeSectionId: undefined,
        languageEditingIndex: undefined,
        languageAdding: false,
      });
      return;
    }
    if (sectionId === worksSection.id) {
      this.setState({
        activeSectionId: undefined,
        worksEditingIndex: undefined,
        worksAdding: false,
        worksFile: undefined,
      });
      return;
    }
  };

  // ============ ListSectionView 所需的附件 setter (模块内自管) ============

  setCertificateFile = (value) => {
    this.setState({ certificateFile: value });
  };

  setWorksFile = (value) => {
    this.setState({ worksFile: value });
  };

  // 教育/技能/语言 不使用附件草稿,提供 no-op 避免破坏 ResumeEditorForm 字段渲染
  setEducationFile = () => {};
  setSkillFile = () => {};
  setLanguageFile = () => {};

  // onTrigger 本模块不涉及选择器 (job/area/intentionJob/intentionArea),保留 no-op
  handleFieldTrigger = () => {};

  // ============ 渲染 ============

  render() {
    const { resumeState = {}, options, applicantId } = this.props;
    const {
      activeSectionId,
      educationEditingIndex, educationAdding,
      skillEditingIndex, skillAdding,
      certificateEditingIndex, certificateAdding, certificateFile,
      languageEditingIndex, languageAdding,
      worksEditingIndex, worksAdding, worksFile,
    } = this.state;

    // 主页面已经把 /applicant/resume_info 的原始数据放在 resumeData.raw,
    // 本模块按 section.id 映射到后端复数键, 直接读 raw.<key>, 不再做归一化.
    // (listKey 字段 —— education / skill / certificate / language / works
    //  已不再由主页面映射到 resumeData 顶层.)
    const raw = (resumeState && resumeState.raw) || {};
    const get = (k) => (Array.isArray(raw[k]) ? raw[k] : []);
    const educationList = get('educations');
    const skillList = get('skills');
    const certificateList = get('certificates');
    const languageList = get('languages');
    const worksList = get('works');

    return (
      <>
        {/* 1) 教育经历 */}
        <ResumeSection
          section={educationSection}
          editing={typeof educationEditingIndex === 'number'}
          adding={educationAdding}
        >
          <ListSectionView
            section={educationSection}
            list={educationList}
            applicantId={applicantId}
            editingIndex={educationEditingIndex}
            adding={educationAdding}
            options={options}
            formRef={this.formRef}
            certificateFile={undefined}
            setCertificateFile={this.setEducationFile}
            worksFile={undefined}
            setWorksFile={this.setEducationFile}
            onTrigger={this.handleFieldTrigger}
            onSubmit={(values) => this.handleSubmit(educationSection, values, educationList[educationEditingIndex])}
            onCancel={() => this.closeEditor(educationSection.id)}
            onEdit={this.startEducationEdit}
            onDelete={(record) => this.handleDelete(educationSection, record)}
            onAdd={this.startEducationAdd}
          />
        </ResumeSection>

        {/* 2) 专业技能 */}
        <ResumeSection
          section={skillSection}
          editing={typeof skillEditingIndex === 'number'}
          adding={skillAdding}
        >
          <ListSectionView
            section={skillSection}
            list={skillList}
            applicantId={applicantId}
            editingIndex={skillEditingIndex}
            adding={skillAdding}
            options={options}
            formRef={this.formRef}
            certificateFile={undefined}
            setCertificateFile={this.setSkillFile}
            worksFile={undefined}
            setWorksFile={this.setSkillFile}
            onTrigger={this.handleFieldTrigger}
            onSubmit={(values) => this.handleSubmit(skillSection, values, skillList[skillEditingIndex])}
            onCancel={() => this.closeEditor(skillSection.id)}
            onEdit={this.startSkillEdit}
            onDelete={(record) => this.handleDelete(skillSection, record)}
            onAdd={this.startSkillAdd}
          />
        </ResumeSection>

        {/* 3) 资格证书 (走 ImageUploader + ListSectionView + 附件) */}
        <ResumeSection
          section={certificateSection}
          editing={typeof certificateEditingIndex === 'number'}
          adding={certificateAdding}
        >
          <ListSectionView
            section={certificateSection}
            list={certificateList}
            applicantId={applicantId}
            editingIndex={certificateEditingIndex}
            adding={certificateAdding}
            options={options}
            formRef={this.formRef}
            certificateFile={certificateFile}
            setCertificateFile={this.setCertificateFile}
            worksFile={undefined}
            setWorksFile={this.setCertificateFile}
            onTrigger={this.handleFieldTrigger}
            onSubmit={(values) => this.handleSubmit(certificateSection, values, certificateList[certificateEditingIndex])}
            onCancel={() => this.closeEditor(certificateSection.id)}
            onEdit={this.startCertificateEdit}
            onDelete={(record) => this.handleDelete(certificateSection, record)}
            onAdd={this.startCertificateAdd}
          />
        </ResumeSection>

        {/* 4) 语言能力 */}
        <ResumeSection
          section={languageSection}
          editing={typeof languageEditingIndex === 'number'}
          adding={languageAdding}
        >
          <ListSectionView
            section={languageSection}
            list={languageList}
            applicantId={applicantId}
            editingIndex={languageEditingIndex}
            adding={languageAdding}
            options={options}
            formRef={this.formRef}
            certificateFile={undefined}
            setCertificateFile={this.setLanguageFile}
            worksFile={undefined}
            setWorksFile={this.setLanguageFile}
            onTrigger={this.handleFieldTrigger}
            onSubmit={(values) => this.handleSubmit(languageSection, values, languageList[languageEditingIndex])}
            onCancel={() => this.closeEditor(languageSection.id)}
            onEdit={this.startLanguageEdit}
            onDelete={(record) => this.handleDelete(languageSection, record)}
            onAdd={this.startLanguageAdd}
          />
        </ResumeSection>

        {/* 5) 个人作品 (走 ImageUploader + ListSectionView + 多图附件) */}
        <ResumeSection
          section={worksSection}
          editing={typeof worksEditingIndex === 'number'}
          adding={worksAdding}
        >
          <ListSectionView
            section={worksSection}
            list={worksList}
            applicantId={applicantId}
            editingIndex={worksEditingIndex}
            adding={worksAdding}
            options={options}
            formRef={this.formRef}
            certificateFile={undefined}
            setCertificateFile={this.setWorksFile}
            worksFile={worksFile}
            setWorksFile={this.setWorksFile}
            onTrigger={this.handleFieldTrigger}
            onSubmit={(values) => this.handleSubmit(worksSection, values, worksList[worksEditingIndex])}
            onCancel={() => this.closeEditor(worksSection.id)}
            onEdit={this.startWorksEdit}
            onDelete={(record) => this.handleDelete(worksSection, record)}
            onAdd={this.startWorksAdd}
          />
        </ResumeSection>
      </>
    );
  }
}

export default EducationAbilityModule;